import dayjs from 'dayjs';
import {AnimatePresence} from 'framer-motion';
import {observer} from 'mobx-react-lite';
import {
  Fragment,
  KeyboardEvent,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useQuery} from 'react-query';
import {useLocation, useNavigate} from 'react-router-dom';
import {
  FiCalendar,
  FiCheck,
  FiChevronDown,
  FiClipboard,
  FiClock,
  FiMinusCircle,
  FiMessageSquare,
  FiArrowRight,
  FiRefreshCw,
  FiSearch,
  FiTarget,
  FiX,
} from 'react-icons/fi';
import {GoalsRequestImpl} from '@api/goals-api/goals-request';
import {useActionItems} from '@hooks/features-action-items';
import {useViewInsight} from '@hooks/insights';
import {usePeriodHook} from '@hooks/period-hook';
import {useFirebaseFetch} from '@hooks/query-hook';
import {useStoreContext} from '@store/store-context';
import {Checkbox} from '@ui/atoms/checkbox';
import {Calendar} from '@ui/molecules/calendar';
import {EmptyPlaceholder} from '@ui/atoms/empty-placeholder';
import {ItemLoader} from '@ui/organisms/item-loader';
import {avatarHueFor} from '@ui/style/avatar-colors';
import {
  goalHealthBarOrder,
  goalListStatusBarColors,
  goalListStatusBarLabels,
} from '@ui/style/goal-status';
import {goalAttentionStatus} from '@utils/goal-status';
import {SuggestedGoalPerformance} from '@utils/goal-status';
import {
  AtRiskIcon,
  BehindIcon,
  MoreVerticalIcon,
  NewOnTrackIcon,
} from '@ui/atoms/icons';
import {FilterIcon} from '@ui/atoms/icons/filter-icon';
import {cloudinary} from '@assets/images/cloudinary';
import {
  activeFilterCount,
  filterMyGoals,
  goalCardDueLabel,
  goalIsEnded,
  goalIsOverdue,
  goalIsOverdueCarryover,
  goalMatchesSearch,
  goalProgress,
  goalScope,
  goalStatusSuggestion,
  goalsFromResponse,
  periodFromValue,
  sortMyGoals,
  standardQuarter,
  summarizeGoalEngagement,
  summarizeMyGoals,
  GoalEngagementSummary,
} from './my-goals-v2.utils';
import {
  emptyMyGoalsFilters,
  MyGoalsFilters,
  MyGoalsLifecycleFilter,
  MyGoalsPeriod,
  MyGoalsPerformanceFilter,
  MyGoalsScopeFilter,
  MyGoalsSort,
  MyGoalsTypeFilter,
} from './my-goals-v2.types';
import {
  AppliedFilters,
  AvatarStack,
  CardEngagementButton,
  CardFooter,
  CardMenu,
  CardMenuButton,
  CardMenuWrap,
  Cards,
  CheckOption,
  ControlButton,
  ControlWrap,
  DueSoon,
  OverdueCarryover,
  DatePopover,
  DateCalendarMenu,
  DateInputButton,
  DatePresetGrid,
  DateSelectButton,
  DateSelectField,
  DateSelectMenu,
  DateSelectOption,
  EmptyPanel,
  EngagementIcon,
  FieldGrid,
  FilterChip,
  FilterCount,
  FilterPopover,
  FilterSection,
  GoalCard,
  GoalCardEnd,
  GoalCardHeader,
  GoalProgressPercent,
  GoalProgressValue,
  GoalPrimary,
  GoalStatusBox,
  GoalTag,
  GoalIdentity,
  GoalLifecycleTag,
  GoalMetadata,
  GoalTitle,
  GoalTitleRow,
  IconButton,
  InitialAvatar,
  KeyResultRow,
  KeyResultDivider,
  KeyResults,
  LifecycleButton,
  LifecycleTabs,
  LoaderPanel,
  NoResults,
  Page,
  PageHeading,
  PaginationBar,
  PerformanceStatus,
  PopoverBody,
  PopoverFooter,
  PopoverHeader,
  PrimaryButton,
  DisclosureButton,
  DisclosureSpacer,
  OwnerCell,
  ResultIdentity,
  ResultValue,
  SmallPrimaryButton,
  SortMenu,
  SortOption,
  StatusBar,
  StatusLegend,
  StatusReviewAction,
  StatusReviewNotice,
  StatusSegment,
  StatusSummary,
  SummaryLabel,
  SummaryPanel,
  SummaryProgress,
  TextButton,
  TinyProgress,
  Toolbar,
  ToolbarDivider,
  ToolbarGroup,
  ToolbarMeta,
  ToolbarPanel,
  ToolbarSearch,
  UnreadIndicator,
} from './my-goals-v2.styles';
import {ProgressRing} from './progress-ring';

const PAGE_SIZE = 10;

const sortLabels: Record<MyGoalsSort, string> = {
  newest: 'Newest first',
  oldest: 'Oldest first',
  highest_progress: 'Highest progress',
  due_date: 'Due date (oldest)',
};

const lifecycleLabels: Array<{
  value: MyGoalsLifecycleFilter;
  label: string;
}> = [
  {value: 'all', label: 'All'},
  {value: 'active', label: 'Active'},
  {value: 'ended', label: 'Ended'},
  {value: 'overdue', label: 'Overdue'},
];

const filterGroups: Array<{
  title: string;
  key: keyof MyGoalsFilters;
  options: Array<{value: string; label: string}>;
}> = [
  {
    title: 'Type',
    key: 'types',
    options: [
      {value: 'objective', label: 'Objective'},
      {value: 'kpi', label: 'KPI'},
    ],
  },
  {
    title: 'Scope',
    key: 'scopes',
    options: [
      {value: 'personal', label: 'Personal'},
      {value: 'team', label: 'Team'},
      {value: 'company', label: 'Company'},
    ],
  },
  {
    title: 'Status',
    key: 'performance',
    options: [
      {value: 'on_track', label: 'On track'},
      {value: 'at_risk', label: 'At risk'},
      {value: 'behind', label: 'Behind'},
      {value: 'upcoming', label: 'Upcoming'},
      {value: 'no_status', label: 'No status'},
    ],
  },
];

const performanceLabels: Record<MyGoalsPerformanceFilter, string> = {
  on_track: 'On track',
  at_risk: 'At risk',
  behind: 'Behind',
  upcoming: 'Upcoming',
  no_status: 'No status',
};

const scopeLabels: Record<MyGoalsScopeFilter, string> = {
  personal: 'Personal',
  team: 'Team',
  company: 'Company-wide',
};

const typeLabels: Record<MyGoalsTypeFilter, string> = {
  objective: 'Objectives',
  kpi: 'KPIs',
};

const initials = (user: any) => {
  const name = user?.firstName
    ? `${user.firstName} ${user.lastName || ''}`
    : user?.name || user?.email || '';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part: string) => part[0])
    .join('')
    .toUpperCase();
};

const userName = (user: any) =>
  user?.firstName
    ? `${user.firstName} ${user.lastName || ''}`.trim()
    : user?.name || user?.email || 'Goal owner';

const userImage = (user: any) => user?.avatar?.url || user?.avatar || '';

const valueWithUnit = (value: any, measurement: any) => {
  const number = Number(value);
  const formatted = Number.isFinite(number)
    ? new Intl.NumberFormat('en-US', {maximumFractionDigits: 2}).format(number)
    : value || 0;
  const unit = String(measurement?.unit || '').toLowerCase();
  if (unit === 'percent' || unit === 'percentage') return `${formatted}%`;
  if (unit === 'currency') {
    const symbol =
      measurement?.symbol === 'USD' ? '$' : measurement?.symbol || '$';
    return `${symbol}${formatted}`;
  }
  if (unit === 'binary') return number >= 1 ? 'Done' : 'Not done';
  return formatted;
};

const useCloseOnOutsideClick = (
  ref: React.RefObject<HTMLElement>,
  close: () => void,
  enabled: boolean,
) => {
  useEffect(() => {
    if (!enabled) return;
    const handleClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) close();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [close, enabled, ref]);
};

const OwnerAvatar = ({owner}: {owner: any}) => (
  <InitialAvatar
    $image={userImage(owner)}
    $avatarHue={avatarHueFor(owner?.id || owner?._id, userName(owner))}
    title={userName(owner)}
    aria-label={userName(owner)}>
    {!userImage(owner) && initials(owner)}
  </InitialAvatar>
);

const ownerList = (goal: any, availableUsers: any[]) => {
  const resolve = (owner: any) => {
    if (typeof owner === 'string') {
      return (
        availableUsers.find((user: any) => user.id === owner) || {id: owner}
      );
    }
    return owner;
  };
  const owners = [
    resolve(goal?.assignee),
    ...(goal?.keyResults || []).map((item: any) => resolve(item?.assignee)),
  ].filter(Boolean);
  return Array.from(
    new Map(
      owners.map((owner: any) => [owner?.id || userName(owner), owner]),
    ).values(),
  );
};

const scopeDescription = (goal: any) => {
  const scope = goalScope(goal);
  if (scope === 'team') {
    const groups = Array.isArray(goal?.group) ? goal.group : [goal?.group];
    const group = groups.find(Boolean);
    return (
      goal?.groupName ||
      goal?.groupType?.name ||
      group?.name ||
      group?.group?.name ||
      'Team'
    );
  }
  return scopeLabels[scope];
};

const CardActions = ({
  goal,
  managerEdit = false,
}: {
  goal: any;
  managerEdit?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const {goalsState} = useStoreContext();
  const framework = goal?.isKpi ? 'KPI' : 'goal';
  const close = useCallback(() => setOpen(false), []);
  useCloseOnOutsideClick(ref, close, open);

  const edit = () => {
    const nextSearch = new URLSearchParams(location.search);
    nextSearch.set(managerEdit ? 'managerEditGoal' : 'editGoalPanel', 'true');
    if (goal?.isKpi) nextSearch.set('kpi', 'true');

    goalsState.setIsManagerEditing(true);
    navigate(`${location.pathname}?${nextSearch.toString()}`, {
      state: {id: goal.id, data: JSON.stringify(goal)},
    });
  };

  const duplicate = () => {
    goalsState.setShowGoalForm(true);
    navigate(
      `/create-goal?duplicate_goal=true${goal?.isKpi ? '&kpi=true' : ''}`,
      {
        state: {id: goal.id, data: JSON.stringify(goal)},
      },
    );
  };

  return (
    <CardMenuWrap ref={ref} onClick={(event) => event.stopPropagation()}>
      <IconButton
        type="button"
        aria-label={`More options for ${goal.name}`}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}>
        <MoreVerticalIcon />
      </IconButton>
      {open && (
        <CardMenu role="menu">
          <CardMenuButton type="button" onClick={edit}>
            Edit {framework}
          </CardMenuButton>
          <CardMenuButton type="button" onClick={duplicate}>
            Duplicate {framework}
          </CardMenuButton>
          <CardMenuButton
            type="button"
            onClick={() => {
              goalsState.setArchiveModal(goal.id, true, goal.name, framework);
              close();
            }}>
            Archive {framework}
          </CardMenuButton>
          {!goalIsEnded(goal) && (
            <CardMenuButton
              type="button"
              $warning
              onClick={() => {
                goalsState.setEndModal(goal.id, goal.name, true, framework);
                close();
              }}>
              End {framework}
            </CardMenuButton>
          )}
          <CardMenuButton
            type="button"
            $danger
            onClick={() => {
              goalsState.setDeleteModal(
                goal.id,
                true,
                goal.name,
                framework,
                goal?.alignedGoals || [],
              );
              close();
            }}>
            Delete {framework}
          </CardMenuButton>
        </CardMenu>
      )}
    </CardMenuWrap>
  );
};

export const MyGoalCard = ({
  goal,
  expanded,
  onToggle,
  availableUsers,
  engagement,
  managerEdit = false,
  canManage = true,
  showCreatedAt = false,
  footerPrompt = 'Want to update or comment?',
  compact = false,
  highlighted = false,
  showResultsDisclosure = true,
  additionalMetadata,
}: {
  goal: any;
  expanded: boolean;
  onToggle: () => void;
  availableUsers: any[];
  engagement: GoalEngagementSummary;
  managerEdit?: boolean;
  canManage?: boolean;
  showCreatedAt?: boolean;
  footerPrompt?: string;
  compact?: boolean;
  highlighted?: boolean;
  showResultsDisclosure?: boolean;
  additionalMetadata?: string;
}) => {
  const viewGoalInsight = useViewInsight();
  const progress = goalProgress(goal);
  const performance = goalAttentionStatus(goal);
  const ended = goalIsEnded(goal);
  const owners = ownerList(goal, availableUsers);
  const keyResults = goal?.keyResults || [];
  const overdue = goalIsOverdue(goal);
  const endDate = goal?.endDate ? dayjs(goal.endDate) : undefined;
  const dueLabel = goalCardDueLabel(goal, progress);

  const openDetails = () => viewGoalInsight(goal.id, Boolean(goal?.isKpi));

  const openStatus = (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    viewGoalInsight(goal.id, Boolean(goal?.isKpi), 'insight', true);
  };

  const viewStatus = (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    openDetails();
  };

  const openEngagementTab = (
    event: ReactMouseEvent<HTMLButtonElement>,
    tab: 'comment' | 'tasks',
  ) => {
    event.stopPropagation();
    viewGoalInsight(goal.id, Boolean(goal?.isKpi), tab);
  };

  const commentTone = engagement.unreadCommentCount > 0 ? 'unread' : 'neutral';
  const actionItemTone =
    engagement.overdueActionItemCount + engagement.dueTodayActionItemCount > 0
      ? 'danger'
      : engagement.dueSoonActionItemCount > 0
      ? 'warning'
      : engagement.allActionItemsCompleted
      ? 'success'
      : engagement.unreadActionItemCount > 0
      ? 'unread'
      : 'neutral';
  const displayedActionItemCount =
    engagement.openActionItemCount || engagement.actionItemCount;
  const actionItemStatus = engagement.overdueActionItemCount
    ? `, ${engagement.overdueActionItemCount} overdue`
    : engagement.dueTodayActionItemCount
    ? `, ${engagement.dueTodayActionItemCount} due today`
    : engagement.dueSoonActionItemCount
    ? `, ${engagement.dueSoonActionItemCount} due soon`
    : engagement.allActionItemsCompleted
    ? ', all completed'
    : '';

  const handleCardKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openDetails();
    }
  };

  const performanceIcon =
    performance === 'overdue' ? (
      <FiClock size={16} />
    ) : performance === 'on_track' ? (
      <NewOnTrackIcon />
    ) : performance === 'at_risk' ? (
      <AtRiskIcon />
    ) : performance === 'behind' ? (
      <BehindIcon />
    ) : performance === 'upcoming' ? (
      <FiClock size={16} />
    ) : (
      <FiMinusCircle size={16} />
    );

  return (
    <GoalCard $compact={compact} $highlighted={highlighted}>
      <GoalCardHeader
        $compact={compact}
        role="button"
        tabIndex={0}
        aria-label={`Open details for ${goal.name}`}
        onClick={openDetails}
        onKeyDown={handleCardKeyDown}>
        <GoalPrimary>
          {!showResultsDisclosure ? null : goal?.isKpi ? (
            <DisclosureSpacer />
          ) : (
            <DisclosureButton
              type="button"
              $expanded={expanded}
              aria-label={`${expanded ? 'Collapse' : 'Expand'} results for ${
                goal.name
              }`}
              aria-expanded={expanded}
              onClick={(event) => {
                event.stopPropagation();
                onToggle();
              }}>
              <FiChevronDown size={18} />
            </DisclosureButton>
          )}
          <GoalIdentity>
            <GoalTitleRow>
              <GoalTag $kpi={Boolean(goal?.isKpi)}>
                {goal?.isKpi ? 'KPI' : 'Goal'}
              </GoalTag>
              {ended && <GoalLifecycleTag>Ended</GoalLifecycleTag>}
              <GoalTitle title={goal?.name}>{goal?.name}</GoalTitle>
            </GoalTitleRow>
            <GoalMetadata>
              <span>
                {scopeDescription(goal)}
                {goal?.isKpi
                  ? ` · ${valueWithUnit(
                      goal.currentValue,
                      goal.measurement,
                    )} / ${valueWithUnit(goal.targetValue, goal.measurement)}`
                  : ` · ${keyResults.length} result${
                      keyResults.length === 1 ? '' : 's'
                    }`}
              </span>
              {showCreatedAt && goal?.createdAt && (
                <span>
                  Created {dayjs(goal.createdAt).format('MMM D, YYYY')}
                </span>
              )}
              {overdue && endDate && (
                <span>Due {endDate.format('MMM D, YYYY')}</span>
              )}
              {additionalMetadata && <span>{additionalMetadata}</span>}
              {(engagement.commentCount > 0 ||
                engagement.unreadCommentCount > 0) && (
                <CardEngagementButton
                  type="button"
                  $tone={commentTone}
                  aria-label={`${engagement.commentCount} comments${
                    engagement.unreadCommentCount
                      ? `, ${engagement.unreadCommentCount} unread`
                      : ''
                  }`}
                  title={
                    engagement.unreadCommentCount
                      ? `${engagement.unreadCommentCount} new comment${
                          engagement.unreadCommentCount === 1 ? '' : 's'
                        }`
                      : `${engagement.commentCount} comment${
                          engagement.commentCount === 1 ? '' : 's'
                        }`
                  }
                  onClick={(event) => openEngagementTab(event, 'comment')}>
                  <EngagementIcon>
                    <FiMessageSquare />
                    {engagement.unreadCommentCount > 0 && <UnreadIndicator />}
                  </EngagementIcon>
                  {engagement.commentCount}
                </CardEngagementButton>
              )}
              {(engagement.actionItemCount > 0 ||
                engagement.unreadActionItemCount > 0) && (
                <CardEngagementButton
                  type="button"
                  $tone={actionItemTone}
                  aria-label={`${engagement.openActionItemCount} open action items${actionItemStatus}`}
                  title={`${engagement.openActionItemCount} open action item${
                    engagement.openActionItemCount === 1 ? '' : 's'
                  }${actionItemStatus}`}
                  onClick={(event) => openEngagementTab(event, 'tasks')}>
                  <EngagementIcon>
                    <FiClipboard />
                    {engagement.unreadActionItemCount > 0 &&
                      actionItemTone === 'unread' && <UnreadIndicator />}
                  </EngagementIcon>
                  {displayedActionItemCount}
                </CardEngagementButton>
              )}
            </GoalMetadata>
          </GoalIdentity>
        </GoalPrimary>

        <OwnerCell>
          <AvatarStack>
            {owners.slice(0, 3).map((owner: any, index) => (
              <OwnerAvatar key={owner?.id || index} owner={owner} />
            ))}
          </AvatarStack>
        </OwnerCell>

        <GoalCardEnd $compact={compact}>
          <GoalStatusBox
            $compact={compact}
            $tone={progress === 100 ? 'success' : undefined}>
            {dueLabel}
            <GoalProgressValue $compact={compact}>
              <TinyProgress $progress={progress} $goal />
              <GoalProgressPercent>{progress}%</GoalProgressPercent>
            </GoalProgressValue>
          </GoalStatusBox>
          <PerformanceStatus
            type="button"
            $performance={performance}
            aria-label={`${
              canManage && !ended ? 'Update' : 'View'
            } status for ${goal.name}`}
            title={canManage && !ended ? 'Update status' : 'View status'}
            onClick={canManage && !ended ? openStatus : viewStatus}>
            {performanceIcon}
            {performance === 'overdue'
              ? 'Overdue'
              : performanceLabels[performance]}
          </PerformanceStatus>
          {canManage && <CardActions goal={goal} managerEdit={managerEdit} />}
        </GoalCardEnd>
      </GoalCardHeader>

      <AnimatePresence initial={false}>
        {showResultsDisclosure &&
          expanded &&
          !goal?.isKpi &&
          keyResults.length > 0 && (
            <KeyResults
              key="key-results"
              initial={{height: 0, opacity: 0}}
              animate={{height: 'auto', opacity: 1}}
              exit={{height: 0, opacity: 0}}
              transition={{duration: 0.3, ease: 'easeInOut'}}>
              {keyResults.map((result: any) => {
                const owner =
                  typeof result?.assignee === 'string'
                    ? availableUsers.find(
                        (user: any) => user.id === result.assignee,
                      )
                    : result?.assignee;
                const resultProgress = goalProgress(result);
                const measurementUnit = String(
                  result?.measurement?.unit || '',
                ).toLowerCase();
                const resultValue =
                  measurementUnit === 'binary'
                    ? valueWithUnit(result.currentValue, result.measurement)
                    : `${valueWithUnit(
                        result.currentValue,
                        result.measurement,
                      )} / ${valueWithUnit(
                        result.targetValue,
                        result.measurement,
                      )}`;
                return (
                  <Fragment key={result.id}>
                    <KeyResultRow>
                      <ResultIdentity>
                        <p title={result.name}>{result.name}</p>
                      </ResultIdentity>
                      <OwnerCell>
                        <OwnerAvatar owner={owner || goal.assignee} />
                      </OwnerCell>
                      <ResultValue>
                        <span>{resultValue}</span>
                        <TinyProgress $compact $progress={resultProgress} />
                        <strong>{resultProgress}%</strong>
                      </ResultValue>
                    </KeyResultRow>
                    <KeyResultDivider />
                  </Fragment>
                );
              })}
              <CardFooter
                type="button"
                onClick={() => viewGoalInsight(goal.id, false)}>
                <span>{footerPrompt}</span>
                <strong>Open details &gt;</strong>
              </CardFooter>
            </KeyResults>
          )}
      </AnimatePresence>
    </GoalCard>
  );
};

export const MyGoalsPage = observer(
  ({onCreateGoal}: {onCreateGoal: () => void}) => {
    const {
      authStore: {auth},
      usersStore: {users},
    } = useStoreContext();
    const {currentMonthRange} = usePeriodHook();
    const firebaseGoals = useFirebaseFetch('goals');
    const goalMentions = useFirebaseFetch(
      `users/${auth.user.id}/mentions/goals`,
    );
    const goalActionItems = useActionItems({
      source: 'goal',
      itemId: '',
      userId: '',
      fetchAll: true,
    });
    const defaultPeriod = useMemo(() => periodFromValue(currentMonthRange()), [
      currentMonthRange,
    ]);
    const [period, setPeriod] = useState<MyGoalsPeriod>(defaultPeriod);
    const [filters, setFilters] = useState<MyGoalsFilters>(emptyMyGoalsFilters);
    const [lifecycle, setLifecycle] = useState<MyGoalsLifecycleFilter>('all');
    const [sort, setSort] = useState<MyGoalsSort>('highest_progress');
    const [searchValue, setSearchValue] = useState('');
    const [page, setPage] = useState(1);
    const [openControl, setOpenControl] = useState<
      'filter' | 'sort' | 'date' | null
    >(null);
    const [expandedGoal, setExpandedGoal] = useState('');
    const [reviewSuggestedOnly, setReviewSuggestedOnly] = useState(false);
    const [draftYear, setDraftYear] = useState(period.year);
    const [draftQuarter, setDraftQuarter] = useState<number | 'custom'>(
      period.quarter,
    );
    const [customStart, setCustomStart] = useState(
      dayjs(period.starts).format('YYYY-MM-DD'),
    );
    const [customEnd, setCustomEnd] = useState(
      dayjs(period.ends).format('YYYY-MM-DD'),
    );
    const [openDateSelect, setOpenDateSelect] = useState<
      'year' | 'quarter' | 'start' | 'end' | null
    >(null);
    const toolbarRef = useRef<HTMLElement>(null);
    const closeControls = useCallback(() => {
      setOpenControl(null);
      setOpenDateSelect(null);
    }, []);
    useCloseOnOutsideClick(toolbarRef, closeControls, openControl !== null);

    const request = useMemo(() => new GoalsRequestImpl(), []);
    const goalsQuery = useQuery(
      ['my-goals-v2', auth?.user?.id],
      () =>
        request.fetchAllGoals(
          {member: auth.user.id, paginate: false},
          1,
          1000000,
        ),
      {
        enabled: Boolean(auth?.user?.id),
        retry: false,
        staleTime: 30000,
      },
    );

    const allGoals = useMemo(() => goalsFromResponse(goalsQuery.data), [
      goalsQuery.data,
    ]);
    const engagementByGoal = useMemo(
      () =>
        allGoals.reduce((summaries, goal) => {
          summaries[goal.id] = summarizeGoalEngagement({
            goal,
            firebaseGoal: firebaseGoals.data?.[goal.id],
            mentions: goalMentions.data?.[goal.id],
            actionItems: goalActionItems.tasks,
            commentsLoading: firebaseGoals.isLoading,
            actionItemsLoading: goalActionItems.isLoading,
          });
          return summaries;
        }, {} as Record<string, GoalEngagementSummary>),
      [
        allGoals,
        firebaseGoals.data,
        firebaseGoals.isLoading,
        goalActionItems.isLoading,
        goalActionItems.tasks,
        goalMentions.data,
      ],
    );
    const periodGoals = useMemo(
      () =>
        filterMyGoals({
          goals: allGoals,
          period,
          filters: emptyMyGoalsFilters,
          lifecycle: 'all',
        }),
      [allGoals, period],
    );
    const carryoverCount = useMemo(
      () =>
        allGoals.filter((goal) => goalIsOverdueCarryover(goal, period)).length,
      [allGoals, period],
    );
    const summary = useMemo(() => summarizeMyGoals(periodGoals), [periodGoals]);
    const statusSuggestionsByGoal = useMemo(
      () =>
        periodGoals.reduce((suggestions, goal) => {
          const suggestion = goalStatusSuggestion(goal);
          if (suggestion) suggestions[goal.id] = suggestion;
          return suggestions;
        }, {} as Record<string, SuggestedGoalPerformance>),
      [periodGoals],
    );
    const statusReviewCount = Object.keys(statusSuggestionsByGoal).length;
    const filteredGoals = useMemo(() => {
      const matchingGoals = filterMyGoals({
        goals: allGoals,
        period,
        filters,
        lifecycle,
        includeOverdueCarryover: true,
      }).filter((goal) => goalMatchesSearch(goal, searchValue));
      return sortMyGoals(
        reviewSuggestedOnly
          ? matchingGoals.filter((goal) => statusSuggestionsByGoal[goal.id])
          : matchingGoals,
        sort,
      );
    }, [
      allGoals,
      filters,
      lifecycle,
      period,
      reviewSuggestedOnly,
      searchValue,
      sort,
      statusSuggestionsByGoal,
    ]);
    const totalPages = Math.max(1, Math.ceil(filteredGoals.length / PAGE_SIZE));
    const visibleGoals = useMemo(
      () => filteredGoals.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
      [filteredGoals, page],
    );

    useEffect(() => setPage(1), [
      filters,
      lifecycle,
      period,
      reviewSuggestedOnly,
      searchValue,
      sort,
    ]);
    useEffect(() => {
      if (reviewSuggestedOnly && statusReviewCount === 0) {
        setReviewSuggestedOnly(false);
      }
    }, [reviewSuggestedOnly, statusReviewCount]);
    useEffect(() => {
      if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);
    useEffect(() => {
      if (
        expandedGoal &&
        !visibleGoals.some((goal) => goal.id === expandedGoal)
      ) {
        setExpandedGoal('');
      }
    }, [expandedGoal, visibleGoals]);

    const filterCount = activeFilterCount(filters);
    const years = useMemo(() => {
      const values = new Set<number>([
        dayjs().year() - 2,
        dayjs().year() - 1,
        dayjs().year(),
        dayjs().year() + 1,
      ]);
      allGoals.forEach((goal) => {
        if (goal?.startDate) values.add(dayjs(goal.startDate).year());
        if (goal?.endDate) values.add(dayjs(goal.endDate).year());
      });
      return Array.from(values).sort((a, b) => b - a);
    }, [allGoals]);

    const toggleFilter = (key: keyof MyGoalsFilters, value: string) => {
      setFilters((current) => {
        const values = current[key] as string[];
        return {
          ...current,
          [key]: values.includes(value)
            ? values.filter((item) => item !== value)
            : [...values, value],
        } as MyGoalsFilters;
      });
    };

    const removeFilter = (key: keyof MyGoalsFilters, value: string) => {
      setFilters(
        (current) =>
          ({
            ...current,
            [key]: (current[key] as string[]).filter((item) => item !== value),
          } as MyGoalsFilters),
      );
    };

    const toggleControl = (control: 'filter' | 'sort' | 'date') => {
      setOpenDateSelect(null);
      if (control === 'date') {
        setDraftYear(period.year);
        setDraftQuarter(period.quarter);
        setCustomStart(dayjs(period.starts).format('YYYY-MM-DD'));
        setCustomEnd(dayjs(period.ends).format('YYYY-MM-DD'));
      }
      setOpenControl((current) => (current === control ? null : control));
    };

    const healthTotal = goalHealthBarOrder.reduce(
      (total, status) => total + summary.byHealth[status],
      0,
    );

    const lifecycleCounts: Record<MyGoalsLifecycleFilter, number> = {
      all: summary.total + carryoverCount,
      active: summary.active,
      ended: summary.ended,
      overdue: summary.overdue + carryoverCount,
    };

    const appliedChips = [
      ...filters.types.map((value) => ({
        key: 'types' as const,
        value,
        label: typeLabels[value],
      })),
      ...filters.scopes.map((value) => ({
        key: 'scopes' as const,
        value,
        label: scopeLabels[value],
      })),
      ...filters.performance.map((value) => ({
        key: 'performance' as const,
        value,
        label: performanceLabels[value],
      })),
    ];

    const periodMeta =
      period.quarter === 'custom'
        ? `${dayjs(period.starts).format('D MMM YYYY')} – ${dayjs(
            period.ends,
          ).format('D MMM YYYY')}`
        : `${period.year} · Q${period.quarter}`;
    const dateSelectionIsDefault =
      draftYear === defaultPeriod.year &&
      draftQuarter === defaultPeriod.quarter &&
      (draftQuarter !== 'custom' ||
        (customStart === dayjs(defaultPeriod.starts).format('YYYY-MM-DD') &&
          customEnd === dayjs(defaultPeriod.ends).format('YYYY-MM-DD')));

    return (
      <Page>
        <PageHeading>
          <div>
            <h1>My Goals</h1>
            <p>Track your goals, progress, and priorities in one place.</p>
          </div>
          {!goalsQuery.isLoading && allGoals.length > 0 && (
            <PrimaryButton type="button" onClick={onCreateGoal}>
              Create a goal
            </PrimaryButton>
          )}
        </PageHeading>

        {goalsQuery.isLoading ? (
          <LoaderPanel>
            <ItemLoader />
          </LoaderPanel>
        ) : goalsQuery.isError ? (
          <NoResults>
            <h2>We couldn’t load your goals</h2>
            <p>Check your connection and try again.</p>
            <SmallPrimaryButton
              type="button"
              onClick={() => goalsQuery.refetch()}>
              Try again
            </SmallPrimaryButton>
          </NoResults>
        ) : allGoals.length === 0 ? (
          <EmptyPanel>
            <EmptyPlaceholder
              title="Set your first goal"
              subtitle="Goals keep you and your team focused on what matters this quarter. Create one to start tracking progress and status in one place."
              image={{
                src: cloudinary.emptyGoals,
                alt: 'A cactus in a plant pot',
                shadow: '-10px 44px 28px 0 rgba(19, 17, 17, 0.12)',
                style: {height: '140px', width: '130px'},
              }}
              style={{margin: '0 auto'}}
              action={
                <PrimaryButton type="button" onClick={onCreateGoal}>
                  Create a goal
                </PrimaryButton>
              }
            />
          </EmptyPanel>
        ) : (
          <>
            <SummaryPanel aria-label="Goals summary">
              <SummaryProgress>
                <ProgressRing progress={summary.averageProgress} />
                <SummaryLabel>
                  Avg. progress
                  <strong>
                    {summary.total} goal{summary.total === 1 ? '' : 's'}
                  </strong>
                </SummaryLabel>
              </SummaryProgress>

              <StatusSummary>
                <h2>Goal health this period</h2>
                <StatusBar aria-label="Goals by health">
                  {goalHealthBarOrder.map((status) => (
                    <StatusSegment
                      key={status}
                      $color={goalListStatusBarColors[status]}
                      $width={
                        healthTotal
                          ? (summary.byHealth[status] / healthTotal) * 100
                          : 0
                      }
                      title={`${summary.byHealth[status]} ${goalListStatusBarLabels[status]}`}
                    />
                  ))}
                </StatusBar>
                <StatusLegend>
                  {goalHealthBarOrder.map((status) => (
                    <span key={status}>
                      <i
                        style={{background: goalListStatusBarColors[status]}}
                      />
                      <strong>{summary.byHealth[status]}</strong>{' '}
                      {goalListStatusBarLabels[status]}
                    </span>
                  ))}
                </StatusLegend>
                {statusReviewCount > 0 && (
                  <StatusReviewNotice $active={reviewSuggestedOnly}>
                    <div>
                      <FiRefreshCw aria-hidden="true" />
                      <span>
                        <strong>{statusReviewCount}</strong>{' '}
                        {statusReviewCount === 1 ? 'goal may' : 'goals may'}{' '}
                        need a status review
                      </span>
                    </div>
                    <StatusReviewAction
                      type="button"
                      disabled={reviewSuggestedOnly}
                      onClick={() => {
                        setFilters(emptyMyGoalsFilters);
                        setLifecycle('active');
                        setReviewSuggestedOnly(true);
                        setPage(1);
                      }}>
                      {reviewSuggestedOnly ? 'Reviewing' : 'Review'}
                      {!reviewSuggestedOnly && (
                        <FiArrowRight aria-hidden="true" />
                      )}
                    </StatusReviewAction>
                  </StatusReviewNotice>
                )}
                {summary.dueSoon > 0 && (
                  <DueSoon>
                    <FiClock /> {summary.dueSoon} due within 14 days
                  </DueSoon>
                )}
                {carryoverCount > 0 && (
                  <OverdueCarryover>
                    <FiClock /> {carryoverCount} overdue from earlier periods
                  </OverdueCarryover>
                )}
              </StatusSummary>

              <LifecycleTabs aria-label="Filter goals by lifecycle">
                {lifecycleLabels.map((item) => (
                  <LifecycleButton
                    key={item.value}
                    type="button"
                    $active={lifecycle === item.value}
                    onClick={() => setLifecycle(item.value)}>
                    {item.label}
                    <span>{lifecycleCounts[item.value]}</span>
                  </LifecycleButton>
                ))}
              </LifecycleTabs>
            </SummaryPanel>

            <ToolbarPanel ref={toolbarRef}>
              <Toolbar>
                <ToolbarGroup>
                  <ToolbarSearch>
                    <FiSearch size={16} aria-hidden="true" />
                    <input
                      type="search"
                      value={searchValue}
                      placeholder="Search goals"
                      aria-label="Search goals"
                      onChange={(event) => setSearchValue(event.target.value)}
                    />
                    {searchValue && (
                      <button
                        type="button"
                        aria-label="Clear search"
                        onClick={() => setSearchValue('')}>
                        <FiX size={14} />
                      </button>
                    )}
                  </ToolbarSearch>
                  <ControlWrap>
                    <ControlButton
                      type="button"
                      $active={openControl === 'filter' || filterCount > 0}
                      aria-expanded={openControl === 'filter'}
                      onClick={() => toggleControl('filter')}>
                      <FilterIcon stroke="currentColor" /> Filter
                      {filterCount > 0 && (
                        <FilterCount>{filterCount}</FilterCount>
                      )}
                      <FiChevronDown />
                    </ControlButton>
                    {openControl === 'filter' && (
                      <FilterPopover>
                        <PopoverBody>
                          <PopoverHeader>
                            <strong>Filter goals</strong>
                            <TextButton
                              type="button"
                              onClick={() => setOpenControl(null)}>
                              Cancel
                            </TextButton>
                          </PopoverHeader>
                          {filterGroups.map((group) => (
                            <FilterSection key={group.key}>
                              <h3>{group.title}</h3>
                              {group.options.map((option) => {
                                const checked = (filters[
                                  group.key
                                ] as string[]).includes(option.value);
                                return (
                                  <CheckOption key={option.value}>
                                    <Checkbox
                                      boxSize="small"
                                      checked={checked}
                                      aria-label={option.label}
                                      onChange={() =>
                                        toggleFilter(group.key, option.value)
                                      }
                                    />
                                    {option.label}
                                  </CheckOption>
                                );
                              })}
                            </FilterSection>
                          ))}
                        </PopoverBody>
                        <PopoverFooter>
                          <TextButton
                            type="button"
                            $active={filterCount > 0}
                            disabled={filterCount === 0}
                            onClick={() => setFilters(emptyMyGoalsFilters)}>
                            Clear all
                          </TextButton>
                        </PopoverFooter>
                      </FilterPopover>
                    )}
                  </ControlWrap>
                </ToolbarGroup>

                <ToolbarGroup>
                  <ControlWrap>
                    <ControlButton
                      type="button"
                      aria-expanded={openControl === 'sort'}
                      onClick={() => toggleControl('sort')}>
                      Sort by <strong>{sortLabels[sort]}</strong>{' '}
                      <FiChevronDown />
                    </ControlButton>
                    {openControl === 'sort' && (
                      <SortMenu $align="right">
                        {(Object.keys(sortLabels) as MyGoalsSort[]).map(
                          (value) => (
                            <SortOption
                              key={value}
                              type="button"
                              $active={sort === value}
                              onClick={() => {
                                setSort(value);
                                setOpenControl(null);
                              }}>
                              {sortLabels[value]}{' '}
                              {sort === value && <FiCheck />}
                            </SortOption>
                          ),
                        )}
                      </SortMenu>
                    )}
                  </ControlWrap>

                  <ToolbarDivider aria-hidden="true" />

                  <ControlWrap>
                    <ControlButton
                      type="button"
                      $active={openControl === 'date'}
                      aria-expanded={openControl === 'date'}
                      onClick={() => toggleControl('date')}>
                      <FiCalendar />{' '}
                      {period.quarter === 'custom' ? 'Custom' : period.label}{' '}
                      <FiChevronDown />
                    </ControlButton>
                    {openControl === 'date' && (
                      <DatePopover $align="right">
                        <PopoverBody>
                          <PopoverHeader>
                            <strong>Select date</strong>
                            <TextButton
                              type="button"
                              onClick={() => {
                                setOpenDateSelect(null);
                                setOpenControl(null);
                              }}>
                              Cancel
                            </TextButton>
                          </PopoverHeader>
                          <DatePresetGrid>
                            <DateSelectField>
                              <span>Year</span>
                              <DateSelectButton
                                type="button"
                                $open={openDateSelect === 'year'}
                                aria-label="Select year"
                                aria-haspopup="listbox"
                                aria-expanded={openDateSelect === 'year'}
                                onClick={() =>
                                  setOpenDateSelect((current) =>
                                    current === 'year' ? null : 'year',
                                  )
                                }>
                                <span>{draftYear}</span>
                                <FiChevronDown size={16} />
                              </DateSelectButton>
                              {openDateSelect === 'year' && (
                                <DateSelectMenu
                                  role="listbox"
                                  aria-label="Year options">
                                  {years.map((year) => (
                                    <DateSelectOption
                                      key={year}
                                      type="button"
                                      role="option"
                                      $active={draftYear === year}
                                      aria-selected={draftYear === year}
                                      onClick={() => {
                                        setDraftYear(year);
                                        setOpenDateSelect(null);
                                      }}>
                                      <span>{year}</span>
                                      {draftYear === year && (
                                        <FiCheck size={14} />
                                      )}
                                    </DateSelectOption>
                                  ))}
                                </DateSelectMenu>
                              )}
                            </DateSelectField>
                            <DateSelectField>
                              <span>Quarter</span>
                              <DateSelectButton
                                type="button"
                                $open={openDateSelect === 'quarter'}
                                aria-label="Select quarter"
                                aria-haspopup="listbox"
                                aria-expanded={openDateSelect === 'quarter'}
                                onClick={() =>
                                  setOpenDateSelect((current) =>
                                    current === 'quarter' ? null : 'quarter',
                                  )
                                }>
                                <span>
                                  {draftQuarter === 'custom'
                                    ? 'Custom range'
                                    : `Q${draftQuarter} ${draftYear}`}
                                </span>
                                <FiChevronDown size={16} />
                              </DateSelectButton>
                              {openDateSelect === 'quarter' && (
                                <DateSelectMenu
                                  role="listbox"
                                  aria-label="Quarter options">
                                  {[1, 2, 3, 4].map((quarter) => (
                                    <DateSelectOption
                                      key={quarter}
                                      type="button"
                                      role="option"
                                      $active={draftQuarter === quarter}
                                      aria-selected={draftQuarter === quarter}
                                      onClick={() => {
                                        setDraftQuarter(quarter);
                                        setOpenDateSelect(null);
                                      }}>
                                      <span>
                                        Q{quarter} {draftYear}
                                      </span>
                                      {draftQuarter === quarter && (
                                        <FiCheck size={14} />
                                      )}
                                    </DateSelectOption>
                                  ))}
                                  <DateSelectOption
                                    type="button"
                                    role="option"
                                    $active={draftQuarter === 'custom'}
                                    aria-selected={draftQuarter === 'custom'}
                                    onClick={() => {
                                      setDraftQuarter('custom');
                                      setOpenDateSelect(null);
                                    }}>
                                    <span>Custom range</span>
                                    {draftQuarter === 'custom' && (
                                      <FiCheck size={14} />
                                    )}
                                  </DateSelectOption>
                                </DateSelectMenu>
                              )}
                            </DateSelectField>
                          </DatePresetGrid>
                          {draftQuarter === 'custom' && (
                            <FieldGrid style={{marginTop: 14}}>
                              <DateSelectField>
                                <span>Starts</span>
                                <DateInputButton
                                  type="button"
                                  $open={openDateSelect === 'start'}
                                  aria-label="Select start date"
                                  aria-haspopup="dialog"
                                  aria-expanded={openDateSelect === 'start'}
                                  onClick={() =>
                                    setOpenDateSelect((current) =>
                                      current === 'start' ? null : 'start',
                                    )
                                  }>
                                  <span>
                                    {customStart
                                      ? dayjs(customStart).format('DD/MM/YYYY')
                                      : 'DD/MM/YYYY'}
                                  </span>
                                  <FiCalendar size={16} />
                                </DateInputButton>
                                {openDateSelect === 'start' && (
                                  <DateCalendarMenu>
                                    <Calendar
                                      value={customStart}
                                      disabledAfter={customEnd || undefined}
                                      onChange={(date) => {
                                        setCustomStart(
                                          dayjs(date).format('YYYY-MM-DD'),
                                        );
                                        setOpenDateSelect(null);
                                      }}
                                    />
                                  </DateCalendarMenu>
                                )}
                              </DateSelectField>
                              <DateSelectField>
                                <span>Ends</span>
                                <DateInputButton
                                  type="button"
                                  $open={openDateSelect === 'end'}
                                  aria-label="Select end date"
                                  aria-haspopup="dialog"
                                  aria-expanded={openDateSelect === 'end'}
                                  onClick={() =>
                                    setOpenDateSelect((current) =>
                                      current === 'end' ? null : 'end',
                                    )
                                  }>
                                  <span>
                                    {customEnd
                                      ? dayjs(customEnd).format('DD/MM/YYYY')
                                      : 'DD/MM/YYYY'}
                                  </span>
                                  <FiCalendar size={16} />
                                </DateInputButton>
                                {openDateSelect === 'end' && (
                                  <DateCalendarMenu $align="right">
                                    <Calendar
                                      value={customEnd}
                                      disabledBefore={customStart || undefined}
                                      onChange={(date) => {
                                        setCustomEnd(
                                          dayjs(date).format('YYYY-MM-DD'),
                                        );
                                        setOpenDateSelect(null);
                                      }}
                                    />
                                  </DateCalendarMenu>
                                )}
                              </DateSelectField>
                            </FieldGrid>
                          )}
                        </PopoverBody>
                        <PopoverFooter>
                          <TextButton
                            type="button"
                            $active={!dateSelectionIsDefault}
                            disabled={dateSelectionIsDefault}
                            onClick={() => {
                              setPeriod(defaultPeriod);
                              setOpenDateSelect(null);
                              setOpenControl(null);
                            }}>
                            Reset to default
                          </TextButton>
                          <SmallPrimaryButton
                            type="button"
                            disabled={
                              draftQuarter === 'custom' &&
                              (!customStart ||
                                !customEnd ||
                                dayjs(customEnd).isBefore(dayjs(customStart)))
                            }
                            onClick={() => {
                              if (draftQuarter === 'custom') {
                                setPeriod({
                                  starts: dayjs(customStart).format(
                                    'MM/DD/YYYY',
                                  ),
                                  ends: dayjs(customEnd).format('MM/DD/YYYY'),
                                  label: 'Custom',
                                  year: dayjs(customStart).year(),
                                  quarter: 'custom',
                                });
                              } else {
                                setPeriod(
                                  standardQuarter(draftYear, draftQuarter),
                                );
                              }
                              setOpenDateSelect(null);
                              setOpenControl(null);
                            }}>
                            Apply
                          </SmallPrimaryButton>
                        </PopoverFooter>
                      </DatePopover>
                    )}
                  </ControlWrap>
                </ToolbarGroup>
              </Toolbar>

              <ToolbarMeta>
                <span>
                  Showing{' '}
                  <strong>
                    {filteredGoals.length} goal
                    {filteredGoals.length === 1 ? '' : 's'}
                  </strong>{' '}
                  for <strong>{periodMeta}</strong>
                </span>
                {(appliedChips.length > 0 || reviewSuggestedOnly) && (
                  <AppliedFilters aria-label="Applied filters">
                    {reviewSuggestedOnly && (
                      <FilterChip
                        type="button"
                        aria-label="Remove status review filter"
                        onClick={() => setReviewSuggestedOnly(false)}>
                        Status review
                        <FiX size={14} />
                      </FilterChip>
                    )}
                    {appliedChips.map((chip) => (
                      <FilterChip
                        key={`${chip.key}-${chip.value}`}
                        type="button"
                        aria-label={`Remove ${chip.label} filter`}
                        onClick={() => removeFilter(chip.key, chip.value)}>
                        {chip.label}
                        <FiX size={14} />
                      </FilterChip>
                    ))}
                    <TextButton
                      type="button"
                      $active
                      onClick={() => {
                        setFilters(emptyMyGoalsFilters);
                        setReviewSuggestedOnly(false);
                      }}>
                      Clear all
                    </TextButton>
                  </AppliedFilters>
                )}
              </ToolbarMeta>
            </ToolbarPanel>

            {visibleGoals.length > 0 ? (
              <>
                <Cards>
                  {visibleGoals.map((goal) => (
                    <MyGoalCard
                      key={goal.id}
                      goal={goal}
                      engagement={engagementByGoal[goal.id]}
                      availableUsers={users}
                      expanded={expandedGoal === goal.id}
                      onToggle={() =>
                        setExpandedGoal((current) =>
                          current === goal.id ? '' : goal.id,
                        )
                      }
                    />
                  ))}
                </Cards>
                {filteredGoals.length > PAGE_SIZE && (
                  <PaginationBar>
                    <span>
                      Page {page} of {totalPages}
                    </span>
                    <div>
                      <button
                        type="button"
                        disabled={page === 1}
                        onClick={() => setPage((value) => value - 1)}>
                        Previous
                      </button>
                      <button
                        type="button"
                        disabled={page === totalPages}
                        onClick={() => setPage((value) => value + 1)}>
                        Next
                      </button>
                    </div>
                  </PaginationBar>
                )}
              </>
            ) : (
              <NoResults>
                <FiTarget size={28} color="#585adf" />
                <h2>
                  {periodGoals.length
                    ? 'No goals match these filters'
                    : 'No goals in this period'}
                </h2>
                <p>
                  {periodGoals.length
                    ? 'Try clearing a filter or choosing a different status.'
                    : 'Choose another reporting period or create a new goal.'}
                </p>
                {periodGoals.length ? (
                  <TextButton
                    type="button"
                    onClick={() => {
                      setFilters(emptyMyGoalsFilters);
                      setLifecycle('all');
                      setReviewSuggestedOnly(false);
                      setSearchValue('');
                    }}>
                    Clear filters
                  </TextButton>
                ) : (
                  <SmallPrimaryButton type="button" onClick={onCreateGoal}>
                    Create a goal
                  </SmallPrimaryButton>
                )}
              </NoResults>
            )}
          </>
        )}
      </Page>
    );
  },
);
